import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AdminLayout } from "@/components/blog/AdminLayout";
import { ProtectedRoute } from "@/components/blog/ProtectedRoute";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Send, RefreshCw, Calendar, DollarSign, Phone, AtSign } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";

export default function AdminFollowUps() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("pending");

  // Fetch all conversations with quotes
  const { data: conversations, isLoading } = useQuery({
    queryKey: ["admin-follow-ups"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("chat_conversations")
        .select("*")
        .not("quoted_price", "is", null)
        .not("user_email", "is", null)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Manually trigger follow-up
  const triggerFollowUp = useMutation({
    mutationFn: async (conversationId: string) => {
      const { data, error } = await supabase.functions.invoke("trigger-follow-up", {
        body: { conversationId },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Follow-up Sent",
        description: "The follow-up email has been sent successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["admin-follow-ups"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send follow-up email",
        variant: "destructive",
      });
    },
  });

  // Mark conversation as converted
  const markAsConverted = useMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await supabase
        .from("chat_conversations")
        .update({ converted_to_booking: true })
        .eq("id", conversationId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Conversation marked as converted to booking.",
      });
      queryClient.invalidateQueries({ queryKey: ["admin-follow-ups"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update conversation",
        variant: "destructive",
      });
    },
  });

  const pendingFollowUps = conversations?.filter(
    (c) => !c.follow_up_sent_at && !c.converted_to_booking
  ) || [];

  const sentFollowUps = conversations?.filter(
    (c) => c.follow_up_sent_at && !c.converted_to_booking
  ) || [];

  const converted = conversations?.filter((c) => c.converted_to_booking) || [];

  const stats = {
    total: conversations?.length || 0,
    pending: pendingFollowUps.length,
    sent: sentFollowUps.length,
    converted: converted.length,
    totalRevenue: conversations?.reduce((sum, c) => sum + (Number(c.quoted_price) || 0), 0) || 0,
  };

  const renderConversationCard = (conversation: any) => (
    <Card key={conversation.id} className="mb-4">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="space-y-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              ${conversation.quoted_price}
              {conversation.converted_to_booking && (
                <Badge variant="default" className="ml-2">Booked</Badge>
              )}
            </CardTitle>
            <CardDescription className="flex flex-col gap-1">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {format(new Date(conversation.created_at), "MMM dd, yyyy 'at' h:mm a")}
              </span>
              {conversation.user_email && (
                <span className="flex items-center gap-2">
                  <AtSign className="h-4 w-4" />
                  {conversation.user_email}
                </span>
              )}
              {conversation.user_phone && (
                <span className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  {conversation.user_phone}
                </span>
              )}
            </CardDescription>
          </div>
          <div className="flex flex-col gap-2 items-end">
            {conversation.follow_up_sent_at && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Followed up {format(new Date(conversation.follow_up_sent_at), "MMM dd")}
              </Badge>
            )}
            <div className="flex gap-2">
              {!conversation.converted_to_booking && (
                <>
                  {!conversation.follow_up_sent_at && (
                    <Button
                      size="sm"
                      onClick={() => triggerFollowUp.mutate(conversation.id)}
                      disabled={triggerFollowUp.isPending}
                    >
                      {triggerFollowUp.isPending ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send Follow-up
                        </>
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => markAsConverted.mutate(conversation.id)}
                    disabled={markAsConverted.isPending}
                  >
                    Mark as Booked
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-sm text-muted-foreground">
          <p><strong>Session ID:</strong> {conversation.session_id}</p>
          <p><strong>Created:</strong> {format(new Date(conversation.created_at), "PPpp")}</p>
          {conversation.ended_at && (
            <p><strong>Ended:</strong> {format(new Date(conversation.ended_at), "PPpp")}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <ProtectedRoute>
      <AdminLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Follow-up Management</h1>
            <p className="text-muted-foreground">
              Manage and track follow-up emails for quoted conversations
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Quotes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Pending Follow-ups
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{stats.pending}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Follow-ups Sent
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{stats.sent}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Converted
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.converted}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${stats.totalRevenue.toFixed(2)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Schedule Info */}
          <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900 dark:text-blue-100">
                <Calendar className="h-5 w-5" />
                Automated Follow-up Schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="text-blue-800 dark:text-blue-200">
              <p className="mb-2">
                <strong>Current Schedule:</strong> Daily at 10:00 AM (Server Time)
              </p>
              <p className="text-sm">
                The system automatically sends follow-up emails to customers who received quotes 
                more than 2 days ago and haven't booked yet. To adjust the schedule, you'll need 
                to update the cron job in the database.
              </p>
            </CardContent>
          </Card>

          {/* Tabs for different views */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="pending">
                Pending Follow-ups ({stats.pending})
              </TabsTrigger>
              <TabsTrigger value="sent">
                Sent Follow-ups ({stats.sent})
              </TabsTrigger>
              <TabsTrigger value="converted">
                Converted ({stats.converted})
              </TabsTrigger>
              <TabsTrigger value="all">
                All Quotes ({stats.total})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="mt-4">
              {isLoading ? (
                <p>Loading...</p>
              ) : pendingFollowUps.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-center text-muted-foreground">
                      No pending follow-ups at the moment
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div>{pendingFollowUps.map(renderConversationCard)}</div>
              )}
            </TabsContent>

            <TabsContent value="sent" className="mt-4">
              {isLoading ? (
                <p>Loading...</p>
              ) : sentFollowUps.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-center text-muted-foreground">
                      No follow-ups sent yet
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div>{sentFollowUps.map(renderConversationCard)}</div>
              )}
            </TabsContent>

            <TabsContent value="converted" className="mt-4">
              {isLoading ? (
                <p>Loading...</p>
              ) : converted.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-center text-muted-foreground">
                      No converted bookings yet
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div>{converted.map(renderConversationCard)}</div>
              )}
            </TabsContent>

            <TabsContent value="all" className="mt-4">
              {isLoading ? (
                <p>Loading...</p>
              ) : conversations?.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-center text-muted-foreground">
                      No conversations with quotes yet
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div>{conversations?.map(renderConversationCard)}</div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </AdminLayout>
    </ProtectedRoute>
  );
}
